import { zodResolver } from '@hookform/resolvers/zod';
import EditRounded from '@mui/icons-material/EditRounded';
import TagRounded from '@mui/icons-material/TagRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../../../api/client';
import { useNotify } from '../../../lib/notify';
import { keys, useNumbering, type Numbering } from '../api';
import {
  applyFieldErrors,
  DOC_TYPE_LABELS,
  NUMBER_TOKENS,
  patternProblems,
  renderNumberPattern,
} from '../lib';
import { SectionCard, SettingSwitch } from '../ui';

const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function NumberingTab({ canManage }: { canManage: boolean }) {
  const { data, isLoading } = useNumbering();
  const [editing, setEditing] = useState<Numbering | null>(null);
  const groups = new Map<string, Numbering[]>();
  for (const n of data?.data ?? []) {
    const g = DOC_TYPE_LABELS[n.docType].group;
    groups.set(g, [...(groups.get(g) ?? []), n]);
  }

  return (
    <SectionCard
      icon={<TagRounded />}
      title="Document numbering"
      description="Prefixes and formats for document numbers. Numbers are never reused or skipped."
    >
      {isLoading ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={3}>
          {[...groups.entries()].map(([group, rows]) => (
            <Box key={group}>
              <Typography variant="overline" color="text.secondary">
                {group}
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '14px',
                  overflow: 'hidden',
                }}
              >
                {rows.map((n) => (
                  <Stack
                    key={`${n.docType}:${n.series}`}
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                      px: 2,
                      py: 1.25,
                      '& + &': { borderTop: 1, borderColor: 'divider' },
                      transition: 'background-color 120ms',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }} noWrap>
                        {DOC_TYPE_LABELS[n.docType].label}
                        {n.series !== 'MAIN' && (
                          <Chip size="small" label={n.series} sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: mono, display: { xs: 'none', sm: 'block' } }}
                      >
                        {n.pattern}
                        {n.resetYearly ? ' · resets yearly' : ''}
                      </Typography>
                    </Box>
                    <Box
                      className="tabular"
                      sx={(t) => {
                        const p = (t.vars ?? t).palette.primary.main;
                        return {
                          fontFamily: mono,
                          fontSize: 13,
                          fontWeight: 600,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: '8px',
                          color: 'primary.main',
                          bgcolor: `color-mix(in srgb, ${p} 8%, transparent)`,
                          border: `1px solid color-mix(in srgb, ${p} 16%, transparent)`,
                          whiteSpace: 'nowrap',
                        };
                      }}
                    >
                      {n.preview}
                    </Box>
                    {canManage && n.series === 'MAIN' && (
                      <Tooltip title="Edit format">
                        <IconButton
                          size="small"
                          aria-label={`Edit ${DOC_TYPE_LABELS[n.docType].label} numbering`}
                          onClick={() => setEditing(n)}
                        >
                          <EditRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
      {editing && <NumberingDialog numbering={editing} onClose={() => setEditing(null)} />}
    </SectionCard>
  );
}

function numberingSchema(minNext: bigint) {
  return z.object({
    prefix: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9/_.-]{1,16}$/, 'Up to 16 letters, digits or / _ . -'),
    pattern: z.string().superRefine((v, ctx) => {
      for (const message of patternProblems(v)) ctx.addIssue({ code: 'custom', message });
    }),
    nextValue: z
      .string()
      .trim()
      .regex(/^[1-9]\d{0,17}$/, 'A whole number of 1 or more')
      .refine((v) => {
        try {
          return BigInt(v) >= minNext;
        } catch {
          return false;
        }
      }, `Can only be raised (currently ${minNext.toString()})`),
    resetYearly: z.boolean(),
  });
}
type Values = z.infer<ReturnType<typeof numberingSchema>>;

function NumberingDialog({ numbering, onClose }: { numbering: Numbering; onClose: () => void }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const patternRef = useRef<HTMLInputElement | null>(null);
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Values>({
    resolver: zodResolver(numberingSchema(BigInt(numbering.nextValue))),
    defaultValues: {
      prefix: numbering.prefix,
      pattern: numbering.pattern,
      nextValue: numbering.nextValue,
      resetYearly: numbering.resetYearly,
    },
  });
  const v = watch();
  const preview = renderNumberPattern(v.pattern, {
    prefix: v.prefix,
    series: numbering.series,
    nextValue: /^\d+$/.test(v.nextValue) ? v.nextValue : numbering.nextValue,
  });

  const insert = (token: string) => {
    const el = patternRef.current;
    const at = el?.selectionStart ?? v.pattern.length;
    const end = el?.selectionEnd ?? at;
    setValue('pattern', v.pattern.slice(0, at) + token + v.pattern.slice(end), {
      shouldDirty: true,
      shouldValidate: true,
    });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  const onSubmit = handleSubmit(async (values) => {
    const body: Record<string, unknown> = {};
    if (dirtyFields.prefix) body.prefix = values.prefix.trim();
    if (dirtyFields.pattern) body.pattern = values.pattern;
    if (dirtyFields.resetYearly) body.resetYearly = values.resetYearly;
    if (values.nextValue !== numbering.nextValue) body.nextValue = values.nextValue.trim();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await unwrap(
        api.PATCH('/settings/numbering/{docType}', {
          params: { path: { docType: numbering.docType } },
          body,
        }),
      );
      await qc.invalidateQueries({ queryKey: keys.numbering });
      notify.success(`${DOC_TYPE_LABELS[numbering.docType].label} numbering updated`);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SEQUENCE_DECREASE') {
        setError('nextValue', { message: 'The next number can only be raised' });
        return;
      }
      if (!applyFieldErrors(e, ['prefix', 'pattern', 'nextValue', 'resetYearly'], setError))
        notify.error(e);
    }
  });

  const { ref: patternFieldRef, ...patternField } = register('pattern');

  return (
    <Dialog open onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {DOC_TYPE_LABELS[numbering.docType].label} numbers
        </DialogTitle>
        <DialogContent>
          <Box
            sx={(t) => {
              const p = (t.vars ?? t).palette.primary;
              return {
                mt: 0.5,
                mb: 2.5,
                p: 2.5,
                borderRadius: '16px',
                textAlign: 'center',
                color: '#fff',
                background: `linear-gradient(135deg, ${p.dark}, ${p.main} 60%, ${p.light})`,
                boxShadow: `0 14px 28px -14px ${p.main}`,
              };
            }}
          >
            <Typography variant="overline" sx={{ opacity: 0.8 }}>
              Next number
            </Typography>
            <Typography
              aria-label="Number preview"
              sx={{
                fontFamily: mono,
                fontSize: { xs: 22, sm: 28 },
                fontWeight: 700,
                letterSpacing: '0.02em',
                wordBreak: 'break-all',
              }}
            >
              {preview || '—'}
            </Typography>
          </Box>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Prefix"
                {...register('prefix')}
                error={Boolean(errors.prefix)}
                helperText={errors.prefix?.message}
                slotProps={{ htmlInput: { maxLength: 16, sx: { fontFamily: mono } } }}
              />
              <TextField
                label="Next value"
                inputMode="numeric"
                {...register('nextValue')}
                error={Boolean(errors.nextValue)}
                helperText={errors.nextValue?.message ?? 'Can be raised, never lowered.'}
                slotProps={{ htmlInput: { sx: { fontFamily: mono } } }}
              />
            </Stack>
            <TextField
              label="Format"
              {...patternField}
              inputRef={(el: HTMLInputElement | null) => {
                patternFieldRef(el);
                patternRef.current = el;
              }}
              error={Boolean(errors.pattern)}
              helperText={errors.pattern?.message ?? 'Click a token below to insert it.'}
              slotProps={{ htmlInput: { maxLength: 64, sx: { fontFamily: mono } } }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {NUMBER_TOKENS.map((t) => (
                <Tooltip key={t.token} title={t.help}>
                  <Chip
                    label={t.token}
                    size="small"
                    variant="outlined"
                    onClick={() => insert(t.token)}
                    sx={{ fontFamily: mono, fontWeight: 600 }}
                  />
                </Tooltip>
              ))}
            </Box>
            <Controller
              control={control}
              name="resetYearly"
              render={({ field }) => (
                <SettingSwitch
                  title="Start again every year"
                  description="The running number goes back to 1 on 1 January. Include {yyyy} or {yy} to keep numbers unique."
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Save format
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
